import { css, cx } from '@emotion/css';
import { useState, useEffect } from 'react';
import type { HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiClock, FiMapPin, FiExternalLink, FiAlertCircle, FiShoppingBag } from 'react-icons/fi';
import { useGetUserOrders } from '../../hooks';
import type { Order } from '../../../../api/services/orders/types';
import { Button } from '../../../../shared/ui/button/button';
import { formatTrustedDateShort, formatTrustedTime } from '../../../../shared/utils/datetime';

// Styles
const container = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-md)',
  },
  '@media (max-width: 360px)': {
    padding: 'var(--spacing-sm)',
  },
});

const header = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--spacing-sm)',
  },
});

const title = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 0,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const list = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
});

const card = css({
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--g-100)',
  borderRadius: 'var(--spacing-xs)',
  border: '1px solid var(--g-200)',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: 'var(--g-300)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-sm)',
  },
});

const cardHeader = css({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 'var(--spacing-md)',
});

const eventDetails = css({
  flex: 1,
});

const eventName = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 'var(--spacing-2xs)',
});

const eventMeta = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-sm)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-3xs)',
  },
});

const metaItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-3xs)',
  fontSize: 'var(--fs-300)',
  color: 'var(--g-600)',
});

const price = css({
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  textAlign: 'right',
  '@media (max-width: 360px)': {
    fontSize: 'var(--fs-400)',
  },
});

const cardFooter = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderTop: '1px solid var(--g-100)',
  paddingTop: 'var(--spacing-sm)',
  '@media (max-width: 480px)': {
    flexWrap: 'wrap',
    gap: 'var(--spacing-sm)',
  },
  '@media (max-width: 360px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

const cardInfo = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-3xs)',
});

const orderId = css({
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  color: 'var(--g-700)',
  '@media (max-width: 360px)': {
    fontSize: 'var(--fs-300)',
  },
});

const orderDate = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--g-500)',
});

const cardActions = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  '@media (max-width: 360px)': {
    width: '100%',
    justifyContent: 'space-between',
    marginTop: 'var(--spacing-xs)',
  },
});

const statusBadge = css({
  fontSize: 'var(--fs-300)',
  fontWeight: 500,
  padding: 'var(--spacing-3xs) var(--spacing-xs)',
  borderRadius: 'var(--spacing-3xs)',
});

const statusSuccess = css({
  backgroundColor: 'rgba(72, 187, 120, 0.1)',
  color: 'rgb(39, 103, 73)',
});

const statusPending = css({
  backgroundColor: 'rgba(251, 191, 36, 0.1)',
  color: 'rgb(146, 64, 14)',
});

const statusFailed = css({
  backgroundColor: 'rgba(245, 101, 101, 0.1)',
  color: 'rgb(155, 44, 44)',
});

const statusDefault = css({
  backgroundColor: 'rgba(var(--g-rgb-500), 0.1)',
  color: 'var(--g-600)',
});

const viewDetails = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--pc-600)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-3xs)',
});

const viewAllContainer = css({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'var(--spacing-md)',
});

const viewAllButton = css({
  backgroundColor: 'transparent',
  border: 'none',
  color: 'var(--pc-600)',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'var(--pc-700)',
    textDecoration: 'underline',
  },
});

const loadingState = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 'var(--spacing-2xl)',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-400)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-xl)',
    fontSize: 'var(--fs-350)',
  },
});

const errorState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(245, 101, 101, 0.1)',
  borderRadius: 'var(--spacing-xs)',
  border: '1px solid rgba(245, 101, 101, 0.3)',
  color: 'rgb(155, 44, 44)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-md)',
  },
});

const errorIcon = css({
  fontSize: '32px',
  '@media (max-width: 480px)': {
    fontSize: '28px',
  },
});

const emptyState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--spacing-2xl) var(--spacing-xl)',
  gap: 'var(--spacing-md)',
  color: 'var(--g-600)',
  textAlign: 'center',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-xl) var(--spacing-md)',
  },
});

const emptyIcon = css({
  fontSize: '48px',
  color: 'var(--g-400)',
  '@media (max-width: 480px)': {
    fontSize: '40px',
  },
});

const emptyText = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-600)',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
  },
});

export interface ProfileOrdersListProps extends HTMLAttributes<HTMLDivElement> {
  limit?: number;
  showTitle?: boolean;
  onOrderClick?: (order: Order) => void;
  onViewAllClick?: () => void;
}

export function ProfileOrdersList({
  limit,
  showTitle = true,
  onOrderClick,
  onViewAllClick,
  className,
  style,
  ...props
}: ProfileOrdersListProps) {
  const navigate = useNavigate();
  const { data: ordersData, isLoading: loading, isError, error: queryError } = useGetUserOrders();
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (ordersData) {
      let processedOrders = [...ordersData];

      // Sort by creation date (newest first)
      processedOrders = processedOrders.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Apply limit if specified
      if (limit && processedOrders.length > limit) {
        processedOrders = processedOrders.slice(0, limit);
      }

      setOrders(processedOrders);
    }
  }, [ordersData, limit]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível';
    return formatTrustedDateShort(dateString);
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    return formatTrustedTime(dateString);
  };

  const formatCurrency = (value: string) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(parseFloat(value));
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'paid':
      case 'pago':
        return statusSuccess;
      case 'pending':
      case 'pendente':
        return statusPending;
      case 'failed':
      case 'cancelled':
      case 'canceled':
      case 'cancelado':
        return statusFailed;
      default:
        return statusDefault;
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'paid':
      case 'pago':
        return 'Pago';
      case 'pending':
      case 'pendente':
        return 'Pendente';
      case 'failed':
        return 'Falha';
      case 'cancelled':
      case 'canceled':
      case 'cancelado':
        return 'Cancelado';
      default:
        return status || 'Desconhecido';
    }
  };

  const handleCardClick = (order: Order) => {
    if (onOrderClick) {
      onOrderClick(order);
    } else {
      // Navigate to order details page
      navigate(`/perfil/compras/${order.id}`);
    }
  };

  if (loading) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        {showTitle && (
          <div className={header}>
            <h2 className={title}>
              <FiShoppingBag />
              Minhas Compras
            </h2>
          </div>
        )}
        <div className={loadingState}>
          <p>Carregando suas compras...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        {showTitle && (
          <div className={header}>
            <h2 className={title}>
              <FiShoppingBag />
              Minhas Compras
            </h2>
          </div>
        )}
        <div className={errorState}>
          <FiAlertCircle className={errorIcon} />
          <p>
            {queryError instanceof Error 
              ? queryError.message 
              : 'Não foi possível carregar suas compras. Tente novamente mais tarde.'}
          </p>
          <Button onClick={() => window.location.reload()}>Tentar novamente</Button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        {showTitle && (
          <div className={header}>
            <h2 className={title}>
              <FiShoppingBag />
              Minhas Compras
            </h2>
          </div>
        )}
        <div className={emptyState}>
          <FiShoppingBag className={emptyIcon} />
          <p className={emptyText}>Você ainda não realizou nenhuma compra</p>
          <Button onClick={() => (window.location.href = '/')}>Explorar eventos</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(container, className)} style={style} {...props}>
      {showTitle && (
        <div className={header}>
          <h2 className={title}>
            <FiShoppingBag />
            {limit ? 'Últimas Compras' : 'Minhas Compras'}
          </h2>
        </div>
      )}

      <div className={list}>
        {orders.map((order) => (
          <div
            key={order.id}
            className={card}
            onClick={() => handleCardClick(order)}
          >
            <div className={cardHeader}>
              <div className={eventDetails}>
                <h4 className={eventName}>Pedido #{order.id.substring(0, 8)}</h4>
                <div className={eventMeta}>
                  <span className={metaItem}>
                    <FiCalendar size={14} />
                    {formatDate(order.createdAt)}
                  </span>
                  <span className={metaItem}>
                    <FiClock size={14} />
                    {formatTime(order.createdAt)}
                  </span>
                  {order.invoice?.paymentMethod && (
                    <span className={metaItem}>
                      <FiMapPin size={14} />
                      {order.invoice.paymentMethod}
                    </span>
                  )}
                </div>
              </div>
              <div className={price}>{formatCurrency(order.totalAmount)}</div>
            </div>

            <div className={cardFooter}>
              <div className={cardInfo}>
                <span className={orderId}>
                  Pagamento #{order.invoice.paymentId}
                </span>
                <span className={orderDate}>
                  Compra realizada em {formatDate(order.createdAt)}
                </span>
              </div>
              <div className={cardActions}>
                <span className={cx(statusBadge, getStatusClass(order.status))}>
                  {getStatusText(order.status)}
                </span>
                <span className={viewDetails}>
                  Ver detalhes <FiExternalLink size={14} />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {limit && orders.length >= limit && onViewAllClick && (
        <div className={viewAllContainer}>
          <Button className={viewAllButton} onClick={onViewAllClick}>
            Ver todas as compras
          </Button>
        </div>
      )}
    </div>
  );
}

export default ProfileOrdersList;
